// src/components/Trading/BulkStrategyMonitor/GenBulkStrategyMonitor.jsx
import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Stack, Chip, IconButton, Button, Grid,
  CircularProgress, Tooltip, Typography, alpha, Avatar,
  Collapse, Alert, AlertTitle, ToggleButtonGroup, ToggleButton, Badge
} from '@mui/material';
import {
  History as HistoryIcon, Timeline as TimelineIcon,
  Refresh as RefreshIcon, Circle as DotIcon,
  Dashboard as DashboardIcon, Bolt as BoltIcon,
  CloudDone as CloudDoneIcon, Speed as SpeedIcon, Sync as SyncIcon,
  CheckCircle as CheckCircleIcon, Warning as WarningIcon,
  Storage as StorageIcon, CloudOff as CloudOffIcon, WifiOff as WifiOffIcon,
  Schedule as ScheduleIcon, TrendingUp as TrendingUpIcon,
  PlayArrow as PlayArrowIcon, Stop as StopIcon
} from '@mui/icons-material';

import AuthContext from '@/contexts/AuthContext';
import { BulkStrategyCard } from '@/components/Trading/BulkStrategyMonitor/components/BulkStrategyCard.jsx';
import { StatCard } from '@/components/Trading/BulkStrategyMonitor/components/StatCard';
import StrategyAlertNotification from '@/components/Trading/BulkStrategyMonitor/components/StrategyAlertNotification';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/';

// Time restriction utility
const isStrategyActiveNow = (strategy) => {
  if (!strategy) return false;
  
  const timeRestriction = strategy.time_restriction || strategy.configuration?.time_restriction;
  if (!timeRestriction || !timeRestriction.enabled) return true;
  
  try {
    const now = new Date();
    const nyFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    
    const nyTimeStr = nyFormatter.format(now);
    const [hours, minutes] = nyTimeStr.split(':').map(Number);
    const currentMinutes = hours * 60 + minutes;
    
    const startTime = timeRestriction.start_time || '09:30';
    const endTime = timeRestriction.end_time || '16:00';
    
    const [startHours, startMins] = startTime.split(':').map(Number);
    const [endHours, endMins] = endTime.split(':').map(Number);
    
    const startMinutes = startHours * 60 + startMins;
    const endMinutes = endHours * 60 + endMins;
    
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  } catch (error) {
    console.error('Time restriction check error:', error);
    return true;
  }
};

// Data Cache
class DataCache {
  constructor(ttl = 60000) {
    this.cache = new Map();
    this.ttl = ttl;
  }

  set(key, value) {
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
    });
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }
    return item.value;
  }

  clear() {
    this.cache.clear();
  }
}

// Request Manager
class RequestManager {
  constructor() {
    this.pendingRequests = new Map();
  }

  async dedupe(key, requestFn) {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }
}

const GenBulkStrategyMonitor = ({ 
  apiBaseUrl = API_BASE_URL,
  onInitialLoadComplete,
  onLoadError 
}) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // State
  const [bulkStrategies, setBulkStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [liveUpdates, setLiveUpdates] = useState([]);
  const [timeframeFilter, setTimeframeFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState(false);
  const [statusCounts, setStatusCounts] = useState(null);
  const [strategyBroadcasts, setStrategyBroadcasts] = useState({});
  const [broadcastsLoading, setBroadcastsLoading] = useState({});
  const [strategyTrades, setStrategyTrades] = useState({});
  const [tradesLoading, setTradesLoading] = useState({});
  const [isOffline, setIsOffline] = useState(false);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [nyCurrentTime, setNyCurrentTime] = useState('');
  const [hiddenByTimeCount, setHiddenByTimeCount] = useState(0);
  const [analyzerSummary, setAnalyzerSummary] = useState(null);

  // Refs
  const dataCache = useRef(new DataCache(60000));
  const requestManager = useRef(new RequestManager());
  const wsRef = useRef(null);
  const wsUpdateTimeoutRef = useRef(null);
  const lastSuccessfulLoadRef = useRef(null);
  const initialLoadRef = useRef(false);
  const mountedRef = useRef(true);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 10;

  // Component mount tracking
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      if (wsUpdateTimeoutRef.current) {
        clearTimeout(wsUpdateTimeoutRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, []);

  // Initial load complete callback
  useEffect(() => {
    if (!loading && !initialLoadRef.current && bulkStrategies.length > 0) {
      initialLoadRef.current = true;
      onInitialLoadComplete?.();
    }
  }, [loading, bulkStrategies.length, onInitialLoadComplete]);

  // NY Time clock
  useEffect(() => {
    const updateNYTime = () => {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/New_York',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
          timeZoneName: 'short',
        });
        setNyCurrentTime(formatter.format(new Date()));
      } catch (error) {
        console.error('Error formatting NY time:', error);
      }
    };

    updateNYTime();
    const interval = setInterval(updateNYTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Add live update
  const addLiveUpdate = useCallback((message, type, data = {}) => {
    if (!mountedRef.current) return;

    const strategyId = data.strategy_id || data.bulk_strategy_id || 'SYSTEM';
    const update = {
      id: Date.now() + Math.random(),
      message: message || 'Update received',
      type: type || 'info',
      timestamp: new Date(),
      strategy_id: strategyId,
      bulk_strategy_id: strategyId,
      symbol: data.symbol,
      signal: data.signal,
      timeframe: data.timeframe,
      raw: data,
    };

    setLiveUpdates(prev => [update, ...prev].slice(0, 50));
  }, []);

  // Transform analyzer response
  const transformAnalyzerResponse = useCallback((data) => {
    if (!data) return null;

    const strategies = [];
    const activeStrategies = data.active_strategies || [];

    activeStrategies.forEach(s => {
      const strategy = {
        bulk_strategy_id: s.strategy_id || s.bulk_strategy_id,
        name: s.name || s.strategy_id,
        symbol: s.symbol || 'NQ',
        timeframe: s.timeframe || '1m',
        direction: s.direction || 'LONG',
        is_active: s.is_active !== false,
        has_data: s.has_data !== false,
        configuration: s.configuration || {},
        time_restriction: s.time_restriction || s.configuration?.time_restriction,
        sub_strategies: s.sub_strategies || [],
        current_state: {
          status: s.status || 'idle',
          in_position: s.in_position || s.status === 'open',
          entry_time: s.entry_time,
          entry_price: s.entry_price,
          trigger_signal: s.trigger_signal,
          pending_confirmations: s.pending_confirmations || [],
          confirmations: s.confirmations || [],
          all_sub_strategies_state: s.sub_strategy_states || {},
        },
        progress: {
          completed: s.completed_count || 0,
          total: s.total_count || s.sub_strategies?.length || 1,
          percentage: s.completion_percentage || 0,
        },
        total_complete_trades: s.total_trades || 0,
      };

      strategies.push(strategy);
    });

    const summary = data.summary || {};
    const statusCounts = {
      idle: summary.idle || 0,
      pending: summary.pending || 0,
      open: summary.open || 0,
      completed: summary.completed || 0,
      has_data: strategies.filter(s => s.has_data).length,
      total: strategies.length,
    };

    return { strategies, statusCounts, summary };
  }, []);

  // Load current state from analyzer
  const loadCurrentState = useCallback(async (broadcast = true) => {
    if (!mountedRef.current) return;

    try {
      const url = broadcast 
        ? `${apiBaseUrl}/strategies/api/analyzer/current-state/?broadcast=true`
        : `${apiBaseUrl}/strategies/api/analyzer/current-state/`;

      const response = await requestManager.current.dedupe('current-state', async () => {
        const res = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      });

      if (!mountedRef.current) return;

      if (response.success) {
        const transformed = transformAnalyzerResponse(response);
        
        if (transformed?.strategies) {
          setBulkStrategies(prev => {
            const updatedMap = new Map();
            (Array.isArray(prev) ? prev : []).forEach(s => {
              updatedMap.set(s.bulk_strategy_id, s);
            });

            transformed.strategies.forEach(s => {
              const existing = updatedMap.get(s.bulk_strategy_id);
              if (existing) {
                updatedMap.set(s.bulk_strategy_id, {
                  ...existing,
                  ...s,
                  current_state: { ...existing.current_state, ...s.current_state },
                  progress: s.progress,
                });
              } else {
                updatedMap.set(s.bulk_strategy_id, s);
              }
            });

            return Array.from(updatedMap.values());
          });

          setStatusCounts(transformed.statusCounts);
          setAnalyzerSummary(transformed.summary);
          setIsOffline(false);

          lastSuccessfulLoadRef.current = {
            strategies: transformed.strategies,
            statusCounts: transformed.statusCounts,
            timestamp: new Date(),
          };

          return transformed;
        }
      }
    } catch (error) {
      if (!mountedRef.current) return;

      const isNetworkError = error.message.includes('fetch') ||
        error.message.includes('network') ||
        error.message.includes('timeout') ||
        error.name === 'TypeError';

      if (isNetworkError) {
        setIsOffline(true);
        if (lastSuccessfulLoadRef.current) {
          addLiveUpdate(`Offline - Using cached data from ${lastSuccessfulLoadRef.current.timestamp.toLocaleTimeString()}`, 'warning', {});
        }
      }

      onLoadError?.(error.message);
      throw error;
    }
  }, [apiBaseUrl, transformAnalyzerResponse, addLiveUpdate, onLoadError]);

  // Load bulk strategies (fallback)
  const loadBulkStrategies = useCallback(async (force = false) => {
    if (!mountedRef.current) return;

    try {
      const cacheKey = `strategies-${timeframeFilter}`;
      if (!force) {
        const cached = dataCache.current.get(cacheKey);
        if (cached) {
          setBulkStrategies(cached.strategies);
          setStatusCounts(cached.statusCounts);
          return cached;
        }
      }

      const response = await fetch(`${apiBaseUrl}/strategies/api/bulk-strategies/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      if (!mountedRef.current) return;

      if (data.success && Array.isArray(data.strategies)) {
        const strategies = data.strategies.map(s => ({
          ...s,
          bulk_strategy_id: s.bulk_strategy_id || s.id,
          current_state: s.current_state || { status: 'idle', in_position: false },
          progress: s.progress || { completed: 0, total: s.sub_strategies?.length || 1, percentage: 0 },
        }));

        const statusCounts = {
          idle: strategies.filter(s => s.current_state?.status === 'idle').length,
          pending: strategies.filter(s => s.current_state?.status === 'pending').length,
          open: strategies.filter(s => s.current_state?.status === 'open').length,
          completed: strategies.filter(s => s.current_state?.status === 'completed').length,
          has_data: strategies.filter(s => s.has_data !== false).length,
          total: strategies.length,
        };

        dataCache.current.set(cacheKey, { strategies, statusCounts });
        setBulkStrategies(strategies);
        setStatusCounts(statusCounts);
        setIsOffline(false);

        lastSuccessfulLoadRef.current = {
          strategies,
          statusCounts,
          timestamp: new Date(),
        };

        return { strategies, statusCounts };
      }
    } catch (error) {
      if (!mountedRef.current) return;

      if (lastSuccessfulLoadRef.current) {
        setBulkStrategies(lastSuccessfulLoadRef.current.strategies);
        setStatusCounts(lastSuccessfulLoadRef.current.statusCounts);
        setIsOffline(true);
      }

      onLoadError?.(error.message);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiBaseUrl, timeframeFilter, onLoadError]);

  // Update strategy from WebSocket
  const updateStrategyFromWebSocket = useCallback((wsData) => {
    if (!mountedRef.current) return;

    const messageType = wsData.type;

    // Handle state_sync
    if (messageType === 'state_sync') {
      const activeStrategies = wsData.active_strategies || [];
      const summary = wsData.summary || {};

      if (activeStrategies.length > 0) {
        const transformed = transformAnalyzerResponse({
          summary,
          active_strategies: activeStrategies,
          timestamp: wsData.timestamp,
        });

        if (transformed?.strategies) {
          setBulkStrategies(prev => {
            const updatedMap = new Map();
            (Array.isArray(prev) ? prev : []).forEach(s => {
              updatedMap.set(s.bulk_strategy_id, s);
            });

            transformed.strategies.forEach(s => {
              const existing = updatedMap.get(s.bulk_strategy_id);
              if (existing) {
                updatedMap.set(s.bulk_strategy_id, {
                  ...existing,
                  ...s,
                  current_state: { ...existing.current_state, ...s.current_state },
                  progress: s.progress,
                });
              } else {
                updatedMap.set(s.bulk_strategy_id, s);
              }
            });

            return Array.from(updatedMap.values());
          });

          setStatusCounts(transformed.statusCounts);
          setAnalyzerSummary(summary);
        }
      }

      addLiveUpdate(`📊 State sync: ${summary.pending || 0} pending, ${summary.open || 0} open`, 'state_sync', wsData);
      return;
    }

    // Handle pending_update
    if (messageType === 'pending_update') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;

      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;

          const completionPct = wsData.completion_percentage || 0;
          const totalSubs = strategy.sub_strategies?.length || 1;
          const completedSubs = Math.round((completionPct / 100) * totalSubs);

          return {
            ...strategy,
            current_state: {
              ...strategy.current_state,
              status: 'pending',
              in_position: false,
              trigger_signal: wsData.trigger_signal,
              pending_confirmations: wsData.pending_confirmations || [],
            },
            progress: {
              completed: completedSubs,
              total: totalSubs,
              percentage: completionPct,
            },
          };
        });
      });

      addLiveUpdate(`⏳ ${strategyId}: ${wsData.completion_percentage}% complete`, 'pending_update', wsData);
      return;
    }

    // Handle bulk_entry
    if (messageType === 'bulk_entry') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;

      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;

          return {
            ...strategy,
            current_state: {
              ...strategy.current_state,
              status: 'open',
              in_position: true,
              entry_time: wsData.entry_time,
              entry_price: wsData.entry_price,
              confirmations: wsData.confirmations,
            },
            progress: {
              completed: strategy.sub_strategies?.length || 1,
              total: strategy.sub_strategies?.length || 1,
              percentage: 100,
            },
          };
        });
      });

      setStrategyTrades(prev => {
        const newTrades = { ...prev };
        delete newTrades[strategyId];
        return newTrades;
      });

      addLiveUpdate(`🟢 ENTRY: ${strategyId} @ $${wsData.entry_price}`, 'bulk_entry', wsData);
      return;
    }

    // Handle bulk_exit
    if (messageType === 'bulk_exit') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;

      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;

          const resetSubStates = {};
          Object.keys(strategy.current_state?.all_sub_strategies_state || {}).forEach(subId => {
            resetSubStates[subId] = {
              ...strategy.current_state.all_sub_strategies_state[subId],
              completed: false,
            };
          });

          return {
            ...strategy,
            current_state: {
              ...strategy.current_state,
              status: 'completed',
              in_position: false,
              exit_time: wsData.exit_time,
              exit_price: wsData.exit_price,
              pnl: wsData.pnl,
              pnl_pct: wsData.pnl_pct,
              all_sub_strategies_state: resetSubStates,
            },
            progress: { completed: 0, total: strategy.sub_strategies?.length || 1, percentage: 0 },
            total_complete_trades: (strategy.total_complete_trades || 0) + 1,
          };
        });
      });

      setStrategyTrades(prev => {
        const newTrades = { ...prev };
        delete newTrades[strategyId];
        return newTrades;
      });

      const pnlSign = wsData.pnl >= 0 ? '+' : '';
      const pnlColor = wsData.pnl >= 0 ? '🟢' : '🔴';
      addLiveUpdate(`${pnlColor} EXIT: ${strategyId} @ $${wsData.exit_price} | P&L: ${pnlSign}${wsData.pnl?.toFixed(2)}`, 'bulk_exit', wsData);
      return;
    }

    // Handle strategy_update
    if (messageType === 'strategy_update') {
      const strategyId = wsData.strategy_id;
      if (!strategyId) return;

      setBulkStrategies(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(strategy => {
          if (strategy.bulk_strategy_id !== strategyId) return strategy;

          const updatedState = { ...strategy.current_state };
          if (wsData.status) updatedState.status = wsData.status;
          if (wsData.entry_time) updatedState.entry_time = wsData.entry_time;
          if (wsData.entry_price) updatedState.entry_price = wsData.entry_price;
          if (wsData.in_position !== undefined) updatedState.in_position = wsData.in_position;
          if (wsData.confirmations) updatedState.confirmations = wsData.confirmations;

          const completionPct = wsData.completion_percentage ?? strategy.progress?.percentage ?? 0;
          const totalSubs = strategy.sub_strategies?.length || 1;
          const completedSubs = Math.round((completionPct / 100) * totalSubs);

          return {
            ...strategy,
            current_state: updatedState,
            progress: { completed: completedSubs, total: totalSubs, percentage: completionPct },
          };
        });
      });

      addLiveUpdate(`📡 ${strategyId}: ${wsData.status || 'updated'}`, 'strategy_update', wsData);
      return;
    }

    if (messageType === 'signal_update') {
      addLiveUpdate(`📶 Signal: ${wsData.signal || wsData.data?.signal || 'unknown'}`, 'signal_update', wsData);
    }
  }, [addLiveUpdate, transformAnalyzerResponse]);

  // WebSocket connection
  useEffect(() => {
    if (!user) {
      console.log('[GenBulkStrategyMonitor] User not authenticated, skipping WebSocket');
      return;
    }

    const connectWebSocket = () => {
      if (!mountedRef.current) return;
      if (wsRef.current?.readyState === WebSocket.OPEN) return;

      try {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const hostname = apiBaseUrl.replace(/^https?:\/\//, '');
        const wsUrl = `${wsProtocol}//${hostname}/ws/bulk-strategy-monitor/`;

        console.log('[GenBulkStrategyMonitor] Connecting to WebSocket:', wsUrl);

        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          if (!mountedRef.current) return;
          console.log('[GenBulkStrategyMonitor] ✅ WebSocket connected');
          setIsWebSocketConnected(true);
          reconnectAttempts.current = 0;
          addLiveUpdate('WebSocket connected', 'connection', {});
        };

        ws.onmessage = (event) => {
          if (!mountedRef.current) return;
          
          try {
            const wsData = JSON.parse(event.data);
            const messageType = wsData.type || 'unknown';

            if (messageType === 'connection' || messageType === 'pong') {
              return;
            }

            console.log('[GenBulkStrategyMonitor] 📨 WS message:', messageType);
            updateStrategyFromWebSocket(wsData);
          } catch (error) {
            console.error('[GenBulkStrategyMonitor] ❌ Parse error:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('[GenBulkStrategyMonitor] ⚠️ WebSocket error:', error);
          if (mountedRef.current) {
            setIsWebSocketConnected(false);
          }
        };

        ws.onclose = () => {
          if (!mountedRef.current) return;
          
          console.log('[GenBulkStrategyMonitor] 🔌 WebSocket closed');
          setIsWebSocketConnected(false);
          
          if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS && user) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
            console.log(`[GenBulkStrategyMonitor] 🔄 Reconnecting in ${delay}ms`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              if (mountedRef.current) {
                reconnectAttempts.current++;
                connectWebSocket();
              }
            }, delay);
          }
        };

        wsRef.current = ws;
      } catch (error) {
        console.error('[GenBulkStrategyMonitor] ❌ Connection failed:', error);
      }
    };

    connectWebSocket();

    // Ping interval to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [user, apiBaseUrl, updateStrategyFromWebSocket, addLiveUpdate]);

  // Load strategy trades
  const loadStrategyTrades = useCallback(async (bulkStrategyId) => {
    if (!mountedRef.current || strategyTrades[bulkStrategyId] || tradesLoading[bulkStrategyId]) return;

    setTradesLoading(prev => ({ ...prev, [bulkStrategyId]: true }));

    try {
      const response = await fetch(
        `${apiBaseUrl}/strategies/api/bulk-strategies/${bulkStrategyId}/trades/`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      if (!mountedRef.current) return;

      if (data.success) {
        setStrategyTrades(prev => ({ ...prev, [bulkStrategyId]: data }));
      }
    } catch (error) {
      console.error(`Trades load failed for ${bulkStrategyId}:`, error);
    } finally {
      if (mountedRef.current) {
        setTradesLoading(prev => ({ ...prev, [bulkStrategyId]: false }));
      }
    }
  }, [apiBaseUrl, strategyTrades, tradesLoading]);

  // Load validated sequences
  const loadValidatedSequences = useCallback(async (bulkStrategyId, force = false) => {
    if (!mountedRef.current) return;
    if (!force && strategyBroadcasts[bulkStrategyId]?.validated) return;

    setBroadcastsLoading(prev => ({ ...prev, [bulkStrategyId]: true }));

    try {
      const response = await fetch(
        `${apiBaseUrl}/strategies/api/bulk-strategies/${bulkStrategyId}/validated-broadcasts/`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (!mountedRef.current) return;

      if (data.success && data.broadcasts) {
        setStrategyBroadcasts(prev => ({
          ...prev,
          [bulkStrategyId]: {
            broadcasts: data.broadcasts,
            validated: true,
            is_historical: true,
            data_age: `${data.lookback_days} days`,
          },
        }));
      }
    } catch (error) {
      console.error(`Broadcasts load failed for ${bulkStrategyId}:`, error);
    } finally {
      if (mountedRef.current) {
        setBroadcastsLoading(prev => ({ ...prev, [bulkStrategyId]: false }));
      }
    }
  }, [apiBaseUrl, strategyBroadcasts]);

  // Handlers
  const handleViewTradeHistory = useCallback((bulkStrategyId) => {
    if (!strategyTrades[bulkStrategyId]) {
      loadStrategyTrades(bulkStrategyId);
    }
    navigate(`/officer/trade-history?strategy=${bulkStrategyId}`);
  }, [strategyTrades, loadStrategyTrades, navigate]);

  const handleManualRefresh = useCallback(async () => {
    setIsOffline(false);
    dataCache.current.clear();
    setLoading(true);

    try {
      await loadCurrentState(true);
    } catch (error) {
      await loadBulkStrategies(true);
    } finally {
      setLoading(false);
    }
  }, [loadCurrentState, loadBulkStrategies]);

  const handleManualSync = useCallback(async () => {
    const strategiesToSync = Array.isArray(bulkStrategies) ? bulkStrategies : [];

    if (strategiesToSync.length === 0) {
      setSyncStatus({ type: 'warning', message: 'No strategies to sync' });
      return;
    }

    if (isOffline) {
      setSyncStatus({ type: 'error', message: 'Cannot sync while offline' });
      return;
    }

    setSyncing(true);
    setSyncStatus({ type: 'info', message: `Syncing ${strategiesToSync.length} strategies...` });

    try {
      const response = await fetch(`${apiBaseUrl}/strategies/api/bulk-strategies/sync-all/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      if (!mountedRef.current) return;

      setSyncStatus({
        type: data.success ? 'success' : 'warning',
        message: data.message || `Synced ${data.synced_count || 0} strategies`,
      });

      setLastSync(new Date());
      await loadCurrentState(true);
    } catch (error) {
      if (!mountedRef.current) return;
      setSyncStatus({ type: 'error', message: `Sync failed: ${error.message}` });
    } finally {
      if (mountedRef.current) {
        setSyncing(false);
      }
    }
  }, [apiBaseUrl, bulkStrategies, loadCurrentState, isOffline]);

  const handleComputeAll = useCallback(async () => {
    if (isOffline) {
      addLiveUpdate('Cannot compute states while offline', 'error', {});
      return;
    }

    setLoading(true);
    addLiveUpdate('Fetching current state from analyzer...', 'info', {});

    try {
      const result = await loadCurrentState(true);

      if (result?.summary) {
        addLiveUpdate(`✅ Loaded ${result.summary.pending || 0} pending, ${result.summary.open || 0} open strategies`, 'success', {});
      }
    } catch (error) {
      addLiveUpdate(`Compute failed: ${error.message}`, 'error', {});
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [isOffline, addLiveUpdate, loadCurrentState]);

  const handleCardExpand = useCallback((strategyId) => {
    setExpandedCards(prev => ({ ...prev, [strategyId]: !prev[strategyId] }));

    if (!strategyBroadcasts[strategyId] && !isOffline) {
      loadValidatedSequences(strategyId, false);
    }
  }, [strategyBroadcasts, loadValidatedSequences, isOffline]);

  // Notification shown callback
  const handleNotificationShown = useCallback((notification) => {
    console.log('[GenBulkStrategyMonitor] 🔔 Alert shown:', notification);
    addLiveUpdate(`🔔 Alert: ${notification.strategyId} - ${notification.type === 'open' ? 'POSITION OPENED' : '100% COMPLETE'}`, 'alert', notification);
  }, [addLiveUpdate]);

  // Initialization
  useEffect(() => {
    const initializeMonitor = async () => {
      if (!mountedRef.current) return;

      setLoading(true);

      try {
        await loadCurrentState(true);
      } catch (error) {
        console.warn('[GenBulkStrategyMonitor] Analyzer failed, falling back to API:', error);
        await loadBulkStrategies(true);
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    initializeMonitor();

    return () => {
      dataCache.current.clear();
    };
  }, [loadCurrentState, loadBulkStrategies]);

  // Periodic refresh
  useEffect(() => {
    if (isOffline || !isWebSocketConnected) return;

    const refreshInterval = setInterval(() => {
      if (mountedRef.current && !loading) {
        loadCurrentState(false).catch(() => {});
      }
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [isOffline, isWebSocketConnected, loading, loadCurrentState]);

  // Filtered strategies
  const filteredStrategies = useMemo(() => {
    const strategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];

    let filtered = strategies;

    const beforeTimeFilter = filtered.length;
    filtered = filtered.filter(s => isStrategyActiveNow(s));
    const afterTimeFilter = filtered.length;

    const hiddenCount = beforeTimeFilter - afterTimeFilter;
    if (hiddenCount !== hiddenByTimeCount) {
      setHiddenByTimeCount(hiddenCount);
    }

    if (timeframeFilter !== 'all') {
      filtered = filtered.filter(s => s.timeframe === timeframeFilter);
    }

    if (positionFilter) {
      filtered = filtered.filter(s => {
        const progress = s.progress?.total > 0
          ? (s.progress.completed / s.progress.total) * 100
          : s.progress?.percentage || 0;
        return progress === 100;
      });
    }

    return filtered.sort((a, b) => {
      const nameA = (a.name || a.bulk_strategy_id || '').toLowerCase();
      const nameB = (b.name || b.bulk_strategy_id || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [bulkStrategies, timeframeFilter, positionFilter, hiddenByTimeCount]);

  // Timeframe groups
  const timeframeGroups = useMemo(() => {
    const groups = {};
    const strategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];

    strategies.forEach(strategy => {
      if (isStrategyActiveNow(strategy)) {
        const tf = strategy.timeframe || 'unknown';
        if (!groups[tf]) groups[tf] = [];
        groups[tf].push(strategy);
      }
    });

    return groups;
  }, [bulkStrategies]);

  // Summary statistics
  const summary = useMemo(() => {
    const allStrategies = Array.isArray(bulkStrategies) ? bulkStrategies : [];
    const visibleStrategies = allStrategies.filter(s => isStrategyActiveNow(s));

    const strategies = timeframeFilter === 'all'
      ? visibleStrategies
      : visibleStrategies.filter(s => s.timeframe === timeframeFilter);

    const with100Percent = strategies.filter(s => {
      const progress = s.progress?.total > 0
        ? (s.progress.completed / s.progress.total) * 100
        : s.progress?.percentage || 0;
      return progress === 100;
    }).length;

    return {
      total: strategies.length,
      total_all_timeframes: visibleStrategies.length,
      active: strategies.filter(s => s.is_active).length,
      in_position: with100Percent,
      pending: analyzerSummary?.pending || strategies.filter(s => s.current_state?.status === 'pending').length,
      open: analyzerSummary?.open || strategies.filter(s => s.current_state?.status === 'open').length,
      completed_strategies: strategies.reduce((sum, s) => sum + (s.progress?.completed || 0), 0),
      total_sub_strategies: strategies.reduce((sum, s) => sum + (s.progress?.total || 0), 0),
      timeframes: Object.keys(timeframeGroups).sort(),
      with_data: strategies.filter(s => s.has_data !== false).length,
      without_data: strategies.filter(s => s.has_data === false).length,
    };
  }, [bulkStrategies, timeframeFilter, timeframeGroups, analyzerSummary]);

  const strategiesCount = summary.total_all_timeframes;

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={48} sx={{ color: '#764ba2' }} />
          <Typography sx={{ color: alpha('#fff', 0.5) }}>Loading strategies...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: '100vh', position: 'relative' }}>
      {/* TradingView-style Notification with Looping Sound */}
      <StrategyAlertNotification 
        strategies={bulkStrategies}
        onNotificationShown={handleNotificationShown}
      />

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: alpha('#1a1f2e', 0.8),
          border: `1px solid ${alpha(isOffline ? '#ff4757' : '#00d4ff', 0.2)}`,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: alpha('#764ba2', 0.2), border: `1px solid ${alpha('#764ba2', 0.3)}` }}>
              <TimelineIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                Bulk Strategy Monitor
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Chip
                  icon={<DotIcon sx={{ fontSize: 12 }} />}
                  label={isWebSocketConnected ? 'LIVE' : 'OFFLINE'}
                  size="small"
                  sx={{
                    bgcolor: alpha(isWebSocketConnected ? '#00ff88' : '#ff4757', 0.15),
                    color: isWebSocketConnected ? '#00ff88' : '#ff4757',
                    fontWeight: 600,
                    height: 22,
                  }}
                />
                {isOffline && (
                  <Chip icon={<WifiOffIcon sx={{ fontSize: 12 }} />} label="NO INTERNET" size="small" sx={{ bgcolor: alpha('#ff4757', 0.15), color: '#ff4757', fontWeight: 600, height: 22 }} />
                )}
                {syncing && (
                  <Chip icon={<CircularProgress size={12} sx={{ color: '#f59e0b' }} />} label="SYNCING" size="small" sx={{ bgcolor: alpha('#f59e0b', 0.15), color: '#f59e0b', fontWeight: 600, height: 22 }} />
                )}
                {analyzerSummary && (
                  <Chip icon={<BoltIcon sx={{ fontSize: 14 }} />} label={`${analyzerSummary.pending || 0} PENDING | ${analyzerSummary.open || 0} OPEN`} size="small" sx={{ bgcolor: alpha('#10b981', 0.15), color: '#10b981', fontWeight: 600, height: 22 }} />
                )}
                {statusCounts && (
                  <Tooltip title={`${statusCounts.has_data} with data`}>
                    <Chip icon={<StorageIcon sx={{ fontSize: 14 }} />} label={`${statusCounts.has_data}/${strategiesCount} DATA`} size="small" sx={{ bgcolor: alpha('#764ba2', 0.15), color: '#764ba2', fontWeight: 600, height: 22 }} />
                  </Tooltip>
                )}
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontSize: '0.75rem' }}>
                  {strategiesCount} strategies active ({summary.timeframes.join(', ')})
                  {hiddenByTimeCount > 0 && (<> • <span style={{ color: '#ffa502' }}>{hiddenByTimeCount} hidden</span></>)} • 
                  NY: <strong style={{ color: '#00d4ff' }}>{nyCurrentTime}</strong> • 
                  Last sync: {lastSync?.toLocaleTimeString() || 'Never'}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <ToggleButtonGroup
              value={timeframeFilter}
              exclusive
              onChange={(e, newFilter) => {
                if (newFilter !== null) {
                  setTimeframeFilter(newFilter);
                  setPositionFilter(false);
                }
              }}
              size="small"
              sx={{
                bgcolor: alpha('#1a1f2e', 0.6),
                '& .MuiToggleButton-root': {
                  color: alpha('#fff', 0.5),
                  border: `1px solid ${alpha('#00d4ff', 0.2)}`,
                  px: 2,
                  '&.Mui-selected': { bgcolor: alpha('#00d4ff', 0.2), color: '#00d4ff', fontWeight: 700 },
                },
              }}
            >
              <ToggleButton value="all">ALL ({strategiesCount})</ToggleButton>
              <ToggleButton value="45s">
                <Badge badgeContent={timeframeGroups['45s']?.length || 0} color="primary" sx={{ '& .MuiBadge-badge': { right: -12, top: -4 } }}>45s</Badge>
              </ToggleButton>
              <ToggleButton value="1m">
                <Badge badgeContent={timeframeGroups['1m']?.length || 0} color="primary" sx={{ '& .MuiBadge-badge': { right: -12, top: -4 } }}>1m</Badge>
              </ToggleButton>
            </ToggleButtonGroup>

            <Tooltip title="View trade history">
              <Button variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={() => navigate('/officer/trade-history')} sx={{ borderColor: alpha('#00d4ff', 0.3), color: '#00d4ff', fontWeight: 600, '&:hover': { borderColor: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>
                History
              </Button>
            </Tooltip>

            <Tooltip title="Load current state from analyzer & broadcast">
              <Button variant="outlined" size="small" startIcon={<BoltIcon />} onClick={handleComputeAll} disabled={loading || isOffline} sx={{ borderColor: alpha('#10b981', 0.3), color: '#10b981', fontWeight: 600, '&:hover': { borderColor: '#10b981', bgcolor: alpha('#10b981', 0.1) }, '&:disabled': { borderColor: alpha('#666', 0.2), color: '#666' } }}>
                Analyze All
              </Button>
            </Tooltip>

            <Tooltip title={isOffline ? 'Cannot sync while offline' : 'Sync from database'}>
              <span>
                <IconButton size="small" onClick={handleManualSync} disabled={syncing || isOffline} sx={{ bgcolor: alpha(isOffline ? '#666' : '#f59e0b', 0.15), border: `1px solid ${alpha(isOffline ? '#666' : '#f59e0b', 0.3)}`, '&:hover': { bgcolor: alpha(isOffline ? '#666' : '#f59e0b', 0.25) }, '&:disabled': { bgcolor: alpha('#666', 0.1) } }}>
                  <SyncIcon sx={{ color: (syncing || isOffline) ? '#666' : '#f59e0b', fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={handleManualRefresh} sx={{ borderColor: alpha('#764ba2', 0.3), color: '#764ba2', fontWeight: 600, '&:hover': { borderColor: '#764ba2', bgcolor: alpha('#764ba2', 0.1) } }}>
              {isOffline ? 'Retry' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts */}
      {isOffline && (
        <Alert severity="warning" icon={<WifiOffIcon />} sx={{ mb: 3, bgcolor: alpha('#ff4757', 0.1), border: `1px solid ${alpha('#ff4757', 0.3)}` }}>
          <AlertTitle sx={{ fontWeight: 700 }}>Offline Mode</AlertTitle>
          {lastSuccessfulLoadRef.current ? (
            <>Network connection lost. Showing cached data from {lastSuccessfulLoadRef.current.timestamp.toLocaleTimeString()}. Click "Retry" to reconnect.</>
          ) : (
            <>No internet connection. Unable to load strategies.</>
          )}
        </Alert>
      )}

      {syncStatus && (
        <Collapse in={!!syncStatus}>
          <Alert
            severity={syncStatus.type}
            icon={syncStatus.type === 'success' ? <CheckCircleIcon /> : <WarningIcon />}
            onClose={() => setSyncStatus(null)}
            sx={{ mb: 3, bgcolor: alpha(syncStatus.type === 'success' ? '#10b981' : syncStatus.type === 'error' ? '#ff4757' : '#f59e0b', 0.1), border: `1px solid ${alpha(syncStatus.type === 'success' ? '#10b981' : syncStatus.type === 'error' ? '#ff4757' : '#f59e0b', 0.3)}` }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>{syncStatus.type === 'success' ? 'Sync Complete' : syncStatus.type === 'error' ? 'Sync Failed' : 'Sync Status'}</AlertTitle>
            {syncStatus.message}
          </Alert>
        </Collapse>
      )}

      {hiddenByTimeCount > 0 && (
        <Alert severity="info" icon={<ScheduleIcon />} sx={{ mb: 3, bgcolor: alpha('#ffa502', 0.05), border: `1px solid ${alpha('#ffa502', 0.2)}` }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{hiddenByTimeCount} {hiddenByTimeCount === 1 ? 'Strategy' : 'Strategies'} Outside Trading Hours</AlertTitle>
          {hiddenByTimeCount} {hiddenByTimeCount === 1 ? 'strategy is' : 'strategies are'} hidden because {hiddenByTimeCount === 1 ? "it's" : "they're"} outside configured trading hours.
        </Alert>
      )}

      {statusCounts && (statusCounts.table_empty > 0 || statusCounts.table_missing > 0) && (
        <Alert severity="info" icon={<CloudOffIcon />} sx={{ mb: 3, bgcolor: alpha('#00d4ff', 0.05), border: `1px solid ${alpha('#00d4ff', 0.2)}` }}>
          <AlertTitle sx={{ fontWeight: 700 }}>Some Strategies Have No Data</AlertTitle>
          {statusCounts.table_missing > 0 && <Typography variant="body2">{statusCounts.table_missing} strategies have missing database tables.</Typography>}
          {statusCounts.table_empty > 0 && <Typography variant="body2">{statusCounts.table_empty} strategies have empty database tables.</Typography>}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Total" value={summary.total} color="#00d4ff" icon={DashboardIcon} subtitle={timeframeFilter === 'all' ? 'All Timeframes' : timeframeFilter} onClick={() => { setPositionFilter(false); setTimeframeFilter('all'); }} isActive={!positionFilter && timeframeFilter === 'all'} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Pending" value={summary.pending} color="#f59e0b" icon={ScheduleIcon} subtitle="Awaiting Confirmations" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Open" value={summary.open} color="#00ff88" icon={BoltIcon} subtitle="In Position" onClick={() => setPositionFilter(!positionFilter)} isActive={positionFilter} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Completed" value={summary.completed_strategies} color="#764ba2" icon={CloudDoneIcon} subtitle={`of ${summary.total_sub_strategies}`} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Progress" value={`${summary.total_sub_strategies > 0 ? ((summary.completed_strategies / summary.total_sub_strategies) * 100).toFixed(0) : 0}%`} color="#ffa502" icon={SpeedIcon} subtitle="Overall" />
        </Grid>
      </Grid>

      {/* Strategy Cards */}
      {filteredStrategies.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', bgcolor: alpha('#1a1f2e', 0.6), border: `1px dashed ${alpha('#00d4ff', 0.2)}`, borderRadius: 2 }}>
          <DashboardIcon sx={{ fontSize: 64, color: alpha('#fff', 0.1), mb: 2 }} />
          <Typography variant="h6" sx={{ color: alpha('#fff', 0.4), mb: 1 }}>
            No Active Strategies {positionFilter && 'with 100% Progress'} {timeframeFilter !== 'all' && `for ${timeframeFilter}`}
          </Typography>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.3) }}>
            {hiddenByTimeCount > 0
              ? `${hiddenByTimeCount} ${hiddenByTimeCount === 1 ? 'strategy is' : 'strategies are'} hidden (outside trading hours)`
              : positionFilter
              ? 'No strategies have reached 100% completion yet'
              : timeframeFilter !== 'all'
              ? 'Try selecting a different timeframe or "ALL"'
              : 'All strategies are outside their trading hours or create a bulk strategy to get started'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2} sx={{ justifyContent: 'flex-start' }}>
          {filteredStrategies.map((strategy) => (
            <Grid item xs={12} md={6} lg={6} xl={4} key={strategy.bulk_strategy_id} sx={{ flexBasis: { md: '45%', lg: '45%', xl: '30%' }, maxWidth: { md: '45% !important', lg: '45% !important', xl: '30% !important' } }}>
              <BulkStrategyCard
                strategy={strategy}
                isExpanded={expandedCards[strategy.bulk_strategy_id] || false}
                onExpandChange={handleCardExpand}
                onOpenDoc={() => {}}
                onViewTradeHistory={handleViewTradeHistory}
                liveUpdates={liveUpdates}
                broadcasts={strategyBroadcasts[strategy.bulk_strategy_id]?.broadcasts || []}
                broadcastsLoading={broadcastsLoading[strategy.bulk_strategy_id] || false}
                isHistorical={strategyBroadcasts[strategy.bulk_strategy_id]?.is_historical || false}
                dataAge={strategyBroadcasts[strategy.bulk_strategy_id]?.data_age || '24 hours'}
                trades={strategyTrades[strategy.bulk_strategy_id]?.trades || []}
                tradesLoading={tradesLoading[strategy.bulk_strategy_id] || false}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default GenBulkStrategyMonitor;
